import React, { useState } from 'react';
import axios from 'axios';
import { RejuvenationCombinedResult } from '../types';
import CombinedTable from './ProcTable';
import HelpTip from './HelpTip';

interface BossFight {
    id: number;
    name: string;
    startTime: number;
    endTime: number;
    difficulty: number;
    type: 'boss' | 'trash';
    trashFightIds?: number[]; // For trash fights, contains the actual fight IDs
}

interface Druid {
    id: number;
    name: string;
    class: string;
}

const RejuvenationProcAnalyzer: React.FC = () => {
    const [logUrl, setLogUrl] = useState('');
    const [bossFights, setBossFights] = useState<BossFight[]>([]);
    const [druids, setDruids] = useState<Druid[]>([]);
    const [selectedDruidId, setSelectedDruidId] = useState<number | null>(null);
    const [selectedFightIds, setSelectedFightIds] = useState<number[]>([]);
    const [result, setResult] = useState<RejuvenationCombinedResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [phase, setPhase] = useState<'url' | 'selection' | 'results'>('url');
    const [error, setError] = useState<string | null>(null);

    const handleUrlChange = async (url: string) => {
        setLogUrl(url);

        if (!url.trim()) {
            setBossFights([]);
            setDruids([]);
            setSelectedDruidId(null);
            setSelectedFightIds([]);
            setPhase('url');
            return;
        }

        // Auto-load fights and druids when URL is pasted
        setLoading(true);
        setError(null);
        setResult(null);
        setBossFights([]);
        setDruids([]);
        setSelectedDruidId(null);
        setSelectedFightIds([]);

        try {
            // Fetch both fights and druids in parallel
            const [fightsResponse, druidsResponse] = await Promise.all([
                axios.post('/api/attendance/get-boss-fights', {
                    logUrl: url,
                }),
                axios.post('/api/attendance/get-druids', {
                    logUrl: url,
                }),
            ]);

            setBossFights(fightsResponse.data.fights);
            setDruids(druidsResponse.data.druids);

            // Auto-select first druid if available
            if (druidsResponse.data.druids.length > 0) {
                setSelectedDruidId(druidsResponse.data.druids[0].id);
            }

            setPhase('selection');
        } catch (err: any) {
            console.error('Error fetching data:', err);
            setError(err.response?.data?.error || 'Failed to fetch data. Please check the URL and try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleFightSelection = (fightId: number, checked: boolean) => {
        if (checked) {
            setSelectedFightIds((prev) => [...prev, fightId]);
        } else {
            setSelectedFightIds((prev) => prev.filter((id) => id !== fightId));
        }
    };

    const handleSelectAll = () => {
        setSelectedFightIds(bossFights.map((fight) => fight.id));
    };

    const handleSelectNone = () => {
        setSelectedFightIds([]);
    };

    const handleProcessAnalysis = async () => {
        if (selectedFightIds.length === 0) {
            setError('Please select at least one fight to analyze');
            return;
        }

        if (selectedDruidId === null) {
            setError('Please select a druid for uptime analysis');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            // Convert selected fight IDs to actual fight IDs
            // If trash fights are selected (id: -1), use the actual trash fight IDs
            const actualFightIds: number[] = [];

            selectedFightIds.forEach((selectedId) => {
                if (selectedId === -1) {
                    // This is the trash fights option
                    const trashFight = bossFights.find((f) => f.id === -1);
                    if (trashFight && trashFight.trashFightIds) {
                        actualFightIds.push(...trashFight.trashFightIds);
                    }
                } else {
                    // This is a regular boss fight
                    actualFightIds.push(selectedId);
                }
            });

            const response = await axios.post('/api/attendance/analyze-rejuvenation-uptime', {
                logUrl,
                selectedFightIds: actualFightIds,
                selectedDruidId,
            });

            setResult(response.data);
            setPhase('results');
        } catch (err: any) {
            console.error('Error analyzing log:', err);
            setError(err.response?.data?.error || 'Failed to analyze log. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setLogUrl('');
        setBossFights([]);
        setDruids([]);
        setSelectedDruidId(null);
        setSelectedFightIds([]);
        setResult(null);
        setError(null);
        setPhase('url');
    };

    const handleBackToSelection = () => {
        setPhase('selection');
        setResult(null);
    };

    const handleBackToUrl = () => {
        setPhase('url');
        setBossFights([]);
        setDruids([]);
        setSelectedDruidId(null);
        setSelectedFightIds([]);
        setResult(null);
    };

    return (
        <div className="rejuvenation-analyzer">
            {phase !== 'results' && (
                <>
                    <h2 className="heading-with-help">
                        Rejuvenation Analysis
                        <HelpTip label="Rejuvenation analysis" wide>
                            <h4>What this shows</h4>
                            <p>Rejuvenation procs and uptime from one Warcraft Logs report.</p>
                            <p>Pick the druid who applied Rejuvenation, then the fights to include.</p>
                        </HelpTip>
                    </h2>
                    <p className="analyzer-description">Rejuvenation procs and uptime from a log.</p>
                </>
            )}

            {phase === 'url' && (
                <div className="url-phase">
                    <div className="analyzer-form">
                        <div className="form-group">
                            <label htmlFor="logUrl">Warcraft Logs URL:</label>
                            <p className="analyzer-description">Paste a Warcraft Logs report URL.</p>
                            <input
                                type="url"
                                id="logUrl"
                                value={logUrl}
                                onChange={(e) => handleUrlChange(e.target.value)}
                                placeholder="https://fresh.warcraftlogs.com/reports/ABC123"
                                className="log-url-input"
                                required
                            />
                        </div>
                        {loading && <div className="loading-message">Loading boss fights...</div>}
                    </div>
                </div>
            )}

            {phase === 'selection' && (
                <div className="selection-phase">
                    <div className="phase-header">
                        <h3>Select Druid and Fights</h3>
                        <button onClick={handleBackToUrl} className="back-button">
                            ← Back to URL
                        </button>
                    </div>

                    <div className="druid-selection">
                        <h4>Select Druid</h4>
                        <div className="druid-dropdown">
                            <select
                                value={selectedDruidId || ''}
                                onChange={(e) => setSelectedDruidId(parseInt(e.target.value) || null)}
                                className="druid-select"
                            >
                                <option value="">Select a druid...</option>
                                {druids.map((druid) => (
                                    <option key={druid.id} value={druid.id}>
                                        {druid.name} ({druid.class})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="fight-selection-controls">
                        <button onClick={handleSelectAll} className="select-button">
                            Select All
                        </button>
                        <button onClick={handleSelectNone} className="select-button">
                            Select None
                        </button>
                        <span className="selection-count">
                            {selectedFightIds.length} of {bossFights.length} fights selected
                        </span>
                    </div>

                    <div className="fight-list">
                        {bossFights.map((fight) => (
                            <div key={fight.id} className="fight-item">
                                <label className="fight-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={selectedFightIds.includes(fight.id)}
                                        onChange={(e) => handleFightSelection(fight.id, e.target.checked)}
                                    />
                                    <span className="fight-name">
                                        {fight.name}
                                        {fight.type === 'trash' && <span className="fight-type-badge">Trash</span>}
                                    </span>
                                    <span className="fight-difficulty">
                                        {fight.type === 'boss' ? `Difficulty: ${fight.difficulty}` : 'Trash Encounters'}
                                    </span>
                                </label>
                            </div>
                        ))}
                    </div>

                    <button
                        onClick={handleProcessAnalysis}
                        disabled={loading || selectedFightIds.length === 0}
                        className="process-button"
                    >
                        {loading ? 'Analyzing...' : 'Process Analysis'}
                    </button>
                </div>
            )}

            {phase === 'results' && result && (
                <div className="results-phase">
                    <div className="phase-header">
                        <h3 className="heading-with-help">
                            Rejuvenation
                            <HelpTip label="Rejuvenation results">
                                <p>Procs and uptime from the selected fights for this druid&apos;s Rejuvenation.</p>
                            </HelpTip>
                        </h3>
                        <button onClick={handleBackToSelection} className="back-button">
                            ← Back to Selection
                        </button>
                    </div>
                    <CombinedTable data={result} />
                </div>
            )}

            {error && (
                <div className="error-message">
                    <p>{error}</p>
                    <button onClick={handleClear} className="clear-button">
                        Clear and Start Over
                    </button>
                </div>
            )}
        </div>
    );
};

export default RejuvenationProcAnalyzer;
